/**
 * LocalStorage, SessionStorage, and Cookie utility functions
 */

import Cookies from 'js-cookie';
import { STORAGE_KEYS } from '../config/constants';

/**
 * LocalStorage utilities
 */
export const storage = {
  /**
   * Set item in localStorage
   */
  set: <T>(key: string, value: T): void => {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  /**
   * Get item from localStorage
   */
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  },

  /**
   * Remove item from localStorage
   */
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },

  /**
   * Clear all items from localStorage
   */
  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },
};

/**
 * Cookie utilities
 */
export const cookieStorage = {
  /**
   * Set cookie
   */
  set: (name: string, value: string, options?: Cookies.CookieAttributes): void => {
    Cookies.set(name, value, {
      expires: 7, // 7 days default
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
      ...options,
    });
  },

  /**
   * Get cookie
   */
  get: (name: string): string | undefined => {
    return Cookies.get(name);
  },

  /**
   * Remove cookie
   */
  remove: (name: string, options?: Cookies.CookieAttributes): void => {
    Cookies.remove(name, options);
  },
};

/**
 * Authentication token utilities
 */
export const tokenStorage = {
  /**
   * Save authentication token (in both localStorage and cookie)
   */
  setToken: (token: string): void => {
    storage.set(STORAGE_KEYS.TOKEN, token);
    cookieStorage.set('auth_token', token, {
      expires: 7, // 7 days
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
    });
  },

  /**
   * Get authentication token (from cookie first, then localStorage)
   */
  getToken: (): string | null => {
    return cookieStorage.get('auth_token') || storage.get<string>(STORAGE_KEYS.TOKEN);
  },

  /**
   * Remove authentication token (from both cookie and localStorage)
   */
  removeToken: (): void => {
    storage.remove(STORAGE_KEYS.TOKEN);
    cookieStorage.remove('auth_token');
  },

  /**
   * Save refresh token
   */
  setRefreshToken: (token: string): void => {
    storage.set(STORAGE_KEYS.REFRESH_TOKEN, token);
    cookieStorage.set('refresh_token', token, {
      expires: 30, // 30 days
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
    });
  },

  /**
   * Get refresh token
   */
  getRefreshToken: (): string | null => {
    return cookieStorage.get('refresh_token') || storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Remove refresh token
   */
  removeRefreshToken: (): void => {
    storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    cookieStorage.remove('refresh_token');
  },

  /**
   * Clear all authentication tokens
   */
  clearAll: (): void => {
    tokenStorage.removeToken();
    tokenStorage.removeRefreshToken();
    storage.remove(STORAGE_KEYS.USER);
  },
};

/**
 * User data utilities
 */
export const userStorage = {
  /**
   * Save user data
   */
  setUser: <T>(user: T): void => {
    storage.set(STORAGE_KEYS.USER, user);
  },

  /**
   * Get user data
   */
  getUser: <T>(): T | null => {
    return storage.get<T>(STORAGE_KEYS.USER);
  },

  /**
   * Remove user data
   */
  removeUser: (): void => {
    storage.remove(STORAGE_KEYS.USER);
  },
};

